import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  BarChart3, Users, DollarSign, ShoppingCart,
  AlertCircle, ArrowRight, Package, FileText, Store
} from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';

const AdminMarketDashboard = ({ userInfo }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // KPI states
  const [totalOrders, setTotalOrders] = useState(0);
  const [activeSellers, setActiveSellers] = useState(0);
  const [todaySales, setTodaySales] = useState(0);
  const [cancelRate, setCancelRate] = useState(0);

  // Action needed states
  const [pendingSellerApps, setPendingSellerApps] = useState(0);
  const [pendingProducts, setPendingProducts] = useState(0);
  const [unpaidSettlements, setUnpaidSettlements] = useState(0);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // 1. marketplaceOrders - total orders, today's sales, cancel rate
      const ordersSnapshot = await getDocs(collection(db, 'marketplaceOrders'));
      const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const totalOrderCount = orders.length;
      setTotalOrders(totalOrderCount);

      // Today's sales calculation
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = today.getTime();

      let todayTotal = 0;
      let cancelledCount = 0;

      orders.forEach(order => {
        // Calculate today's sales
        const orderDate = order.createdAt?.toDate?.() || (order.createdAt ? new Date(order.createdAt) : null);
        if (orderDate && orderDate.getTime() >= todayTimestamp) {
          todayTotal += (order.totalAmount || order.amount || 0);
        }
        // Count cancelled orders
        if (order.status === 'cancelled' || order.status === 'canceled') {
          cancelledCount++;
        }
      });

      setTodaySales(todayTotal);
      setCancelRate(totalOrderCount > 0 ? Math.round((cancelledCount / totalOrderCount) * 100 * 10) / 10 : 0);

      // 2. marketplaceProducts - count pending review products
      const pendingProductsQuery = query(
        collection(db, 'marketplaceProducts'),
        where('status', '==', 'pending')
      );
      const pendingProductsSnapshot = await getDocs(pendingProductsQuery);
      setPendingProducts(pendingProductsSnapshot.size);

      // 3. sellerApplications - count pending applications
      const pendingAppsQuery = query(
        collection(db, 'sellerApplications'),
        where('status', '==', 'pending')
      );
      const pendingAppsSnapshot = await getDocs(pendingAppsQuery);
      setPendingSellerApps(pendingAppsSnapshot.size);

      // Active sellers count (approved seller applications)
      const allAppsSnapshot = await getDocs(collection(db, 'sellerApplications'));
      const approvedSellers = allAppsSnapshot.docs.filter(doc => doc.data().status === 'approved');
      setActiveSellers(approvedSellers.length);

      // 4. marketplaceSettlements - count unpaid settlements
      const unpaidSettlementsQuery = query(
        collection(db, 'marketplaceSettlements'),
        where('status', '==', 'pending')
      );
      const unpaidSettlementsSnapshot = await getDocs(unpaidSettlementsQuery);
      setUnpaidSettlements(unpaidSettlementsSnapshot.size);

    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userInfo) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }
    if (!isAdminUser(userInfo)) {
      alert('관리자만 접근 가능합니다.');
      navigate('/dashboard');
      return;
    }
    loadDashboardData();
  }, [userInfo, navigate, loadDashboardData]);

  const formatCurrency = (amount) => {
    if (amount == null) return '0원';
    return amount.toLocaleString() + '원';
  };

  // Access denied screen
  if (!isAdminUser(userInfo)) {
    return (
      <div style={styles.accessDenied}>
        <AlertCircle size={48} color="#ef4444" />
        <h2 style={styles.accessDeniedTitle}>접근 권한 없음</h2>
        <p style={styles.accessDeniedText}>관리자만 이 페이지에 접근할 수 있습니다.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>대시보드 데이터를 불러오는 중...</p>
      </div>
    );
  }

  const kpiCards = [
    {
      icon: <ShoppingCart size={24} color="#6366f1" />,
      iconBg: '#e0e7ff',
      value: totalOrders.toLocaleString(),
      label: '총 주문 수',
      sub: '전체 누적 주문',
    },
    {
      icon: <Users size={24} color="#10b981" />,
      iconBg: '#d1fae5',
      value: activeSellers.toLocaleString(),
      label: '활성 판매자',
      sub: '승인된 판매자 수',
    },
    {
      icon: <DollarSign size={24} color="#f59e0b" />,
      iconBg: '#fef3c7',
      value: formatCurrency(todaySales),
      label: '오늘 거래액',
      sub: '금일 거래 합계',
    },
    {
      icon: <BarChart3 size={24} color="#ef4444" />,
      iconBg: '#fee2e2',
      value: cancelRate + '%',
      label: '취소율',
      sub: '전체 주문 대비',
    },
  ];

  const actionItems = [
    {
      icon: <Store size={20} color="#6366f1" />,
      iconBg: '#e0e7ff',
      label: '판매자 승인 대기',
      count: pendingSellerApps,
      path: '/admin/seller-approval',
      description: '신규 판매자 신청이 대기 중입니다',
    },
    {
      icon: <Package size={20} color="#f59e0b" />,
      iconBg: '#fef3c7',
      label: '상품 검수 대기',
      count: pendingProducts,
      path: '/admin/product-approval',
      description: '등록된 상품이 검수를 기다리고 있습니다',
    },
    {
      icon: <FileText size={20} color="#10b981" />,
      iconBg: '#d1fae5',
      label: '미정산 건',
      count: unpaidSettlements,
      path: '/admin/settlement-management',
      description: '정산이 완료되지 않은 건이 있습니다',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <BarChart3 size={28} color="#6366f1" />
            DC 마켓 운영 대시보드
          </h1>
          <p style={styles.subtitle}>마켓플레이스 운영 현황을 한눈에 확인하세요</p>
        </div>
        <button style={styles.refreshBtn} onClick={loadDashboardData}>
          새로고침
        </button>
      </div>

      {/* KPI Cards */}
      <div style={styles.cardsGrid}>
        {kpiCards.map((card, idx) => (
          <div key={idx} style={styles.dashCard}>
            <div style={styles.dashCardHeader}>
              <div style={{ ...styles.dashCardIcon, backgroundColor: card.iconBg }}>
                {card.icon}
              </div>
            </div>
            <div style={styles.dashCardValue}>{card.value}</div>
            <div style={styles.dashCardLabel}>{card.label}</div>
            <div style={styles.dashCardSub}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Action Needed Section */}
      <div style={styles.actionSection}>
        <h2 style={styles.sectionTitle}>
          <AlertCircle size={22} color="#6366f1" />
          처리 필요 항목
        </h2>
        <div style={styles.actionGrid}>
          {actionItems.map((item, idx) => (
            <div
              key={idx}
              style={styles.actionCard}
              onClick={() => navigate(item.path)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.15)';
                e.currentTarget.style.borderColor = '#6366f1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              <div style={styles.actionCardTop}>
                <div style={styles.actionCardLeft}>
                  <div style={{ ...styles.actionIconWrap, backgroundColor: item.iconBg }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={styles.actionLabel}>{item.label}</div>
                    <div style={styles.actionDesc}>{item.description}</div>
                  </div>
                </div>
                <div style={styles.actionCardRight}>
                  <div style={styles.actionCount}>{item.count}</div>
                  <ArrowRight size={18} color="#6366f1" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '8px 0 0 0',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Loading
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: '#64748b',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  // Access denied
  accessDenied: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
  },
  accessDeniedTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#0f172a',
    margin: '16px 0 8px 0',
  },
  accessDeniedText: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },

  // KPI Cards
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
  },
  dashCard: {
    padding: '24px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    transition: 'box-shadow 0.2s',
  },
  dashCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  dashCardIcon: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
  },
  dashCardValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#0f172a',
    marginBottom: '4px',
  },
  dashCardLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
  },
  dashCardSub: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },

  // Action needed section
  actionSection: {
    marginBottom: '32px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
    fontWeight: '700',
    color: '#0f172a',
    margin: '0 0 16px 0',
  },
  actionGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 24px',
    backgroundColor: 'white',
    borderRadius: '14px',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  actionCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionCardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  actionIconWrap: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
  },
  actionLabel: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '2px',
  },
  actionDesc: {
    fontSize: '13px',
    color: '#94a3b8',
  },
  actionCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  actionCount: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#6366f1',
  },
};

export default AdminMarketDashboard;
